import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import LoadingSpinner from '@/components/LoadingSpinner';
import { 
  ChevronLeft,
  User,
  Mail,
  Phone,
  Camera,
  Save,
  Edit,
  Shield,
  Award,
  Star,
  MapPin,
  Calendar,
  Clock,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useGuestApi } from '@/hooks/useRoleBasedApi';
import { useToast } from "@/hooks/use-toast";
import UnifiedMobileFooter from '@/components/UnifiedMobileFooter';

interface UserProfileData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  avatar?: string;
}

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const guestApi = useGuestApi();
  
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false); // Circuit breaker to prevent multiple simultaneous retries

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const loadProfile = useCallback(async (isRetry = false) => {
    try {
      if (!isRetry) {
        setLoading(true);
        setError(null);
      }
      
      // Add timeout handling and retry logic
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 15000); // 15 second timeout
      });

      const profilePromise = guestApi.getProfile();
      
      const profileData = await Promise.race([profilePromise, timeoutPromise]) as UserProfileData;
      
      console.log('UserProfile - Profile data loaded successfully:', profileData);
      
      setProfile(profileData);
      setFormData({
        name: profileData.name || "",
        email: profileData.email || "",
        phone: profileData.phone || "",
      });
      setRetryCount(0);
      setError(null);
    } catch (error) {
      console.error('UserProfile - Failed to load profile:', error);
      
      // Handle specific error types
      if (error instanceof Error) {
        if (error.message.includes('timeout') || error.message.includes('timeout of 10000ms exceeded')) {
          const errorMsg = 'Backend is taking too long to respond. Please try again.';
          console.error('BACKEND ISSUE: timeout of 15000ms exceeded');
          setError(errorMsg);
          
          // Auto-retry logic (max 3 attempts) - ONLY for initial load, not retries
          if (retryCount < 2 && !isRetry && !isRetrying) {
            const newRetryCount = retryCount + 1;
            setRetryCount(newRetryCount);
            setIsRetrying(true); // Circuit breaker
            toast({
              title: "Retrying...",
              description: `Attempt ${newRetryCount + 1} of 3`,
              variant: "default",
            });
            
            // Wait 2 seconds before retrying
            setTimeout(() => {
              loadProfile(true);
              setIsRetrying(false); // Reset circuit breaker
            }, 2000);
            return;
          } else if (retryCount >= 2) {
            // Max retries reached - show final error
            setError('Maximum retry attempts reached. Please check your connection and try again later.');
            console.error('BACKEND ISSUE: Maximum retry attempts reached');
          }
        } else if (error.message.includes('Failed to fetch')) {
          console.error('BACKEND ISSUE: Network error - backend may be down or CORS not configured');
          setError('Network error - please check your connection and try again');
        } else if (error.message.includes('401')) {
          console.error('BACKEND ISSUE: Authentication error - user session may be invalid');
          setError('Authentication error - please log in again');
        } else if (error.message.includes('404')) {
          console.error('BACKEND ISSUE: API endpoint not found - check if /profile endpoint exists');
          setError('Profile endpoint not found - please contact support');
        } else if (error.message.includes('500')) {
          console.error('BACKEND ISSUE: Server error - backend may have crashed');
          setError('Server error - please try again later');
        } else {
          console.error('BACKEND ISSUE:', error.message);
          setError('Failed to load profile - please try again');
        }
      }
      
      // Set empty profile on error
      setProfile(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
      });
    } finally {
      setLoading(false);
    }
  }, [guestApi, toast]); // Removed retryCount dependency to prevent infinite loops

  useEffect(() => {
    console.log('UserProfile - useEffect triggered, user:', user?.id);
    if (user) {
      loadProfile();
    }
  }, [user, loadProfile]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      await guestApi.updateProfile(formData);
      
      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      
      // Reload profile data
      await loadProfile();
    } catch (error) {
      console.error('UserProfile - Failed to save profile:', error);
      toast({
        title: "Error",
        description: "Failed to save profile changes",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('avatar', file);
      
      await guestApi.updateAvatar(formData);
      
      toast({
        title: "Success",
        description: "Avatar updated successfully",
      });
      
      // Reload profile data
      await loadProfile();
    } catch (error) {
      console.error('UserProfile - Failed to upload avatar:', error);
      toast({
        title: "Error",
        description: "Failed to upload avatar",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    setError(null);
    setIsRetrying(false); // Reset circuit breaker
    loadProfile();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="xl" />
          <p className="mt-4 text-gray-600">Loading profile...</p>
          {retryCount > 0 && (
            <p className="mt-2 text-sm text-gray-500">Attempt {retryCount + 1} of 3</p>
          )}
        </div>
      </div>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Edit Profile | SaunaDip</title>
        <meta name="description" content="Edit your SaunaDip profile" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50 pb-32 md:pb-0">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="p-2"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-semibold text-gray-900">Edit Profile</h1>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-6">
          {error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load profile</h3>
              <p className="text-gray-500 text-sm mb-6">{error}</p>
              <div className="space-y-3">
                <Button onClick={handleRetry} className="bg-sauna-orange hover:bg-sauna-orange/90">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/dashboard')}
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          ) : profile ? (
            <div className="space-y-6">
              {/* Avatar Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5" />
                    Profile Photo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <Avatar className="w-20 h-20">
                      <AvatarImage src={profile.avatar} alt={profile.name} />
                      <AvatarFallback className="text-lg">
                        {profile.name?.charAt(0)?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <input
                        type="file"
                        id="avatar-upload"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                      <label htmlFor="avatar-upload">
                        <Button variant="outline" asChild>
                          <span>
                            <Camera className="w-4 h-4 mr-2" />
                            Change Photo
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Profile Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Profile Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Enter your email address"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Enter your phone number"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Save Button */}
              <div className="pt-4">
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="w-full bg-sauna-orange hover:bg-sauna-orange/90"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <p className="text-gray-500 mb-4">Failed to load profile</p>
                <Button onClick={handleRetry} className="bg-sauna-orange hover:bg-sauna-orange/90">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <UnifiedMobileFooter />
    </>
  );
};

export default UserProfile; 